// Speech-to-video timing model used to approximate a generation's COST (credits)
// and the placeholder timeline clip length, kept in sync. The backend charges
// `ceil(realVideoSeconds × creditPerSecond)`, so the estimate's job is to predict
// realVideoSeconds from the speech text. Calibrated against real dev generations
// (speaker 165, en-US, speed 1, 720p): speech runs at ~12 characters per second
// of video PLUS a fixed ~1s of lead/trail silence per clip. Measured seconds vs
// chars: 9→1.66s, 141→11.9s, 410→35.1s, 674→56.2s.
//
// The previous flat rate (18 chars/s, no overhead) under-estimated EVERY clip —
// worst for short ones, where the fixed silence dominates (9 chars: quoted 2
// credits, charged 7) — so users saw a cost far below what the server charged and
// hit "not enough credits" mid-generation. A rate of 12 (below the ~12.9 steady
// rate) plus the overhead keeps the estimate at/just-above real without ever
// under-quoting across the calibration set. See PROGRESS_CHARS_PER_SECOND (also
// 12) for the render bar's rate — same speaking rate, but that path re-estimates
// from real durations later so it carries no overhead.
export const AVATAR_CHARS_PER_SECOND = 12
export const AVATAR_BASE_OVERHEAD_SECONDS = 1

// Estimated seconds of avatar video for a given speech string (trimmed).
// Returns 0 for empty/blank text. Models real TTS timing as a fixed per-clip
// overhead plus a steady speaking rate: overhead + length / rate. Not rounded —
// callers round as needed (cost ceils after multiplying by credit; duration
// clamps to a minimum).
export const estimateAvatarSeconds = (text: string): number => {
  const trimmed = text.trim()
  if (!trimmed) return 0
  return AVATAR_BASE_OVERHEAD_SECONDS + trimmed.length / AVATAR_CHARS_PER_SECOND
}

// --- Generation-progress estimation ------------------------------------------
//
// The backend (recommendation-api) splits the speech script into "screens" and
// renders each screen as a separate short video, then stitches them. The split
// rule (recommendation-api `AI_AVATARS`): pack whole sentences greedily until a
// part would exceed ~AVATAR_MAX_SUB_VIDEO_SECONDS (20s) of speech, i.e.
// ~AVATAR_MAX_CHARS_PER_SCREEN characters. Each screen is one render job; in
// production screens render in parallel across workers, in a single-server dev
// environment they queue. We mirror that split client-side so the simulated
// progress bar scales its expected duration with the real screen count instead
// of assuming one screen — which is why a long script used to hit 100% early.
//
// All values below are ESTIMATES for the fake progress bar only; the server does
// the authoritative split/render. They are centralized here so they can be
// calibrated against observed real generations in one place.

// Backend `AI_AVATARS.AVATAR_MAX_SUB_VIDEO_SECONDS` × `AVG_CHARS_PER_SECOND`
// (20 × 20 = 400): the max characters of speech the backend packs into one
// screen before starting a new one.
export const AVATAR_MAX_CHARS_PER_SCREEN = 400

// Sentence terminators the greedy packer breaks on. The backend uses an LLM that
// only splits at sentence boundaries; we approximate with punctuation + newlines.
// A run of text with no terminator is treated as a single sentence (and may
// exceed the char budget — it still becomes its own screen, matching the backend
// "never split mid-sentence" rule).
const SENTENCE_SPLIT_REGEX = /[^.!?…\n]+[.!?…\n]*/g

// Splits speech text into the sentence chunks the greedy packer groups. Falls
// back to the whole trimmed string when no terminator is present.
const splitIntoSentences = (text: string): string[] => {
  const matches = text.match(SENTENCE_SPLIT_REGEX)
  if (!matches) return []
  return matches.map(s => s.trim()).filter(Boolean)
}

// Estimates how many screens the backend will split this script into: greedily
// pack consecutive sentences while the running character count stays within
// AVATAR_MAX_CHARS_PER_SCREEN; start a new screen when the next sentence would
// overflow. A single sentence longer than the budget still counts as one screen
// (the backend never splits mid-sentence). Always >= 1 for non-blank text.
export const estimateAvatarScreenCount = (text: string): number => {
  const sentences = splitIntoSentences(text)
  if (!sentences.length) return 1

  let screens = 1
  let currentLength = 0

  for (const sentence of sentences) {
    // +1 approximates the space/newline joining sentences within a screen.
    const addition = currentLength === 0 ? sentence.length : sentence.length + 1
    if (
      currentLength > 0 &&
      currentLength + addition > AVATAR_MAX_CHARS_PER_SCREEN
    ) {
      screens += 1
      currentLength = sentence.length
    } else {
      currentLength += addition
    }
  }

  return screens
}

// Tunable timing model for the two-phase progress bar. Phase 1 (prep: Gemini
// script split + TTS voiceover + credit charge) runs until the real
// `voiceover_generated` socket event; phase 2 (render + stitch) runs until the
// real `finished` event.
export const AVATAR_PROGRESS_CONFIG = {
  // Progress % the bar is allowed to reach during phase 1. The
  // `voiceover_generated` event snaps/advances it to this boundary and hands off
  // to phase 2. Kept modest because prep is short relative to render.
  VOICEOVER_PHASE_PERCENT: 35,

  // Cap the render phase climbs to before the real `finished` event snaps it to
  // 100%. Matches the service's own EXPECTED_TOTAL_PERCENTAGE (99).
  RENDER_PHASE_CAP_PERCENT: 99,

  // Speech characters per second used to estimate a screen's VIDEO DURATION for
  // the render lookup (verified against a real generation: 62 chars → ~5.75s of
  // video ⇒ ~11–12 chars/s, matching the backend's cost rate). Same speaking rate
  // as AVATAR_CHARS_PER_SECOND (the cost/placeholder estimate), but that path adds
  // a fixed per-clip overhead and this one does not — this estimate is only the
  // start-of-generation seed for the progress bar, which re-estimates from the
  // real per-screen durations at `voiceover_generated`. A too-fast rate here makes
  // screens look shorter, so the render lookup picks too small a duration.
  PROGRESS_CHARS_PER_SECOND: 12,

  // Phase 1 (prep) estimate, seconds. TTS runs with backend concurrency 5, so
  // it grows slowly with screen count rather than linearly. Calibrated against a
  // real 3-screen dev generation where prep (generate → voiceover_generated) took
  // ~35s (15 + 7×3 = 36s).
  PREP_BASE_SECONDS: 15,
  PREP_PER_SCREEN_SECONDS: 7,

  // Phase 2 (render) estimate. Generation-wide overhead + stitch
  // (RENDER_BASE_SECONDS), plus per-screen render time blended for parallelism.
  // FALLBACK ONLY — used when the backend per-(duration, quality) render time
  // isn't available (catalog not loaded). The primary source is the model's
  // `generationAvgDurationSeconds` (see `estimateAvatarGenerationPhases` +
  // aiAvatar/selectors). Per-screen render = RENDER_FIXED_PER_SCREEN_SECONDS (a
  // large fixed setup cost per talking-head screen) + video-length ×
  // RENDER_SECONDS_PER_VIDEO_SECOND. Calibrated against two real dev generations:
  // a 3.4s-of-video screen rendered in ~71s and a ~17.7s screen in ~142s ⇒ ~50s
  // fixed + ~5s per video-second.
  // PARALLELISM_FACTOR blends fully-parallel (factor→0: total ≈ one screen's
  // render, extra screens are free) and fully-sequential (factor→1: total ≈ sum).
  // Observation: a real 3-screen render took ~626s ≈ ONE screen's backend value
  // (~540s), i.e. screens render mostly in PARALLEL across workers — so keep this
  // low (a small serialization/queue buffer), NOT near 1. Bias slightly up rather
  // than to 0 so a busy queue doesn't make the bar stall at 99 (over-estimate →
  // `finished` snaps it up early, which is the more honest failure mode). Note:
  // observed render time is highly variable (~2×) run-to-run, so this is
  // approximate by nature.
  RENDER_BASE_SECONDS: 15,
  RENDER_FIXED_PER_SCREEN_SECONDS: 50,
  RENDER_SECONDS_PER_VIDEO_SECOND: 5,
  PARALLELISM_FACTOR: 0.3
} as const

export interface IAvatarPhaseEstimate {
  screens: number
  prepSeconds: number
  renderSeconds: number
}

// Maps a screen's video length (seconds) to the model's average render seconds
// for the currently selected quality — the backend `generationAvgDurationSeconds`,
// keyed by the catalog's discrete durations (see aiAvatarSelectors
// `avatarRenderSecondsByDuration`). Picks the NEAREST available catalog duration
// so a real per-screen length that falls between (or outside) catalog keys still
// resolves to a real value instead of dropping into a gap. Returns undefined only
// when the catalog is empty (not loaded), so callers fall back to the calibrated
// model.
export const buildRenderSecondsLookup =
  (renderSecondsByDuration: Map<number, number>) =>
  (videoSeconds: number): number | undefined => {
    const durations = [...renderSecondsByDuration.keys()]
    if (!durations.length) return undefined
    const nearest = durations.reduce((best, duration) =>
      Math.abs(duration - videoSeconds) < Math.abs(best - videoSeconds)
        ? duration
        : best
    )
    return renderSecondsByDuration.get(nearest)
  }

// One screen's render time: the backend per-(duration, quality) average when the
// lookup yields a positive value, otherwise the calibrated fallback (fixed
// per-screen setup cost + a video-length-proportional part).
const perScreenRenderSecondsFor = (
  videoSeconds: number,
  renderSecondsForVideoDuration?: (videoSeconds: number) => number | undefined
): number => {
  const { RENDER_FIXED_PER_SCREEN_SECONDS, RENDER_SECONDS_PER_VIDEO_SECOND } =
    AVATAR_PROGRESS_CONFIG
  const backendPerScreen = renderSecondsForVideoDuration?.(videoSeconds)
  return backendPerScreen && backendPerScreen > 0
    ? backendPerScreen
    : RENDER_FIXED_PER_SCREEN_SECONDS +
        videoSeconds * RENDER_SECONDS_PER_VIDEO_SECOND
}

// Blends per-screen render times into a single wall-clock estimate: the slowest
// screen is the critical path (screens render in parallel across workers), plus a
// PARALLELISM_FACTOR-discounted contribution from the rest (factor→0 fully
// parallel, →1 fully sequential). For N equal screens this reduces exactly to the
// original `r * (1 + PARALLELISM_FACTOR * (N - 1))`.
const blendRenderWorkSeconds = (perScreenRenderSeconds: number[]): number => {
  if (!perScreenRenderSeconds.length) return 0
  const max = Math.max(...perScreenRenderSeconds)
  const sum = perScreenRenderSeconds.reduce((total, s) => total + s, 0)
  return max + AVATAR_PROGRESS_CONFIG.PARALLELISM_FACTOR * (sum - max)
}

// Render-phase duration (seconds) from the ACTUAL per-screen video durations the
// backend reports at `voiceover_generated` — one entry per screen. Each real
// screen length is mapped to its backend render time and blended for parallelism.
// This replaces the text-based approximation (`estimateAvatarGenerationPhases`)
// once the real durations are known, so the render bar tracks reality. Floored at
// 1s.
export const estimateAvatarRenderSecondsFromScreens = ({
  screenVideoSeconds,
  renderSecondsForVideoDuration
}: {
  screenVideoSeconds: number[]
  renderSecondsForVideoDuration?: (videoSeconds: number) => number | undefined
}): number => {
  const perScreen = screenVideoSeconds.map(videoSeconds =>
    perScreenRenderSecondsFor(videoSeconds, renderSecondsForVideoDuration)
  )
  const renderWorkSeconds = blendRenderWorkSeconds(perScreen)
  return Math.max(
    1,
    AVATAR_PROGRESS_CONFIG.RENDER_BASE_SECONDS + renderWorkSeconds
  )
}

// Estimates the two generation phases' durations for the progress bar from the
// speech text, returning the screen count and the prep/render second estimates.
// Used at generation START, before the real per-screen durations are known; once
// `voiceover_generated` reports them, the render phase is re-estimated with
// `estimateAvatarRenderSecondsFromScreens` (see socket handler).
//
// The render phase is driven by the BACKEND: `renderSecondsForVideoDuration` maps
// a screen's video length (seconds) to the model's average render seconds for
// that (duration, quality) — i.e. `generationAvgDurationSeconds`, the same field
// AI Video uses as its expected duration. Each avatar screen is one such video,
// so per-screen render = that lookup, blended across screens for parallelism. The
// calibrated `AVATAR_PROGRESS_CONFIG` render constants are only a FALLBACK for
// when the catalog isn't loaded / the lookup returns nothing. Both durations are
// floored at 1s.
export const estimateAvatarGenerationPhases = ({
  speechText,
  renderSecondsForVideoDuration
}: {
  speechText: string
  renderSecondsForVideoDuration?: (videoSeconds: number) => number | undefined
}): IAvatarPhaseEstimate => {
  const {
    PREP_BASE_SECONDS,
    PREP_PER_SCREEN_SECONDS,
    PROGRESS_CHARS_PER_SECOND
  } = AVATAR_PROGRESS_CONFIG

  const screens = estimateAvatarScreenCount(speechText)
  // Estimate video seconds at the real speech rate (NOT MOCK_CHARS_PER_SECOND,
  // which is faster and would make screens look shorter than they render), split
  // evenly across the estimated screens (all equal length, since we can't know
  // the real per-screen split yet).
  const totalVideoSeconds = speechText.trim().length / PROGRESS_CHARS_PER_SECOND
  const perScreenVideoSeconds = totalVideoSeconds / screens

  const renderSeconds = estimateAvatarRenderSecondsFromScreens({
    screenVideoSeconds: Array<number>(screens).fill(perScreenVideoSeconds),
    renderSecondsForVideoDuration
  })

  const prepSeconds = Math.max(
    1,
    PREP_BASE_SECONDS + PREP_PER_SCREEN_SECONDS * screens
  )

  return { screens, prepSeconds, renderSeconds }
}
